import React, { useCallback, useEffect } from 'react';
import {
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Brush,
    AreaChart,
    Area,
    ResponsiveContainer,
    ReferenceLine,
} from 'recharts';
import styles from '../assets/graphic.module.scss';
import { useCurrentPng } from 'recharts-to-png';
import FileSaver from 'file-saver';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import PageItem from '../../screens/General/screens/Cases/screens/CaseList/components/react-table/PageItem';

interface PlotProps {
    data: { seanceDate: string; answer: number; prompts: string }[];
    target: any;
}

const Plot: React.FunctionComponent<PlotProps> = ({ data, target }: PlotProps) => {
    // useCurrentPng usage (isLoading is optional)
    const [getAreaPng, { ref: areaRef, isLoading }] = useCurrentPng();

    // Can also pass in options for html2canvas
    // const [getPng, { ref }] = useCurrentPng({ backgroundColor: '#000' });

    const handleDownload = useCallback(async () => {
        const png = await getAreaPng();

        // Verify that png is not undefined
        if (png) {
            FileSaver.saveAs(png, 'myChart.png');
        }
    }, [getAreaPng]);

    const getIntroOfPage = (label: string) => {
        if (label === 'Page A') {
            return "Page A is about men's clothing";
        }
        if (label === 'Page B') {
            return "Page B is about women's dress";
        }
        return 'test';
    };
    const CustomTooltip = (object: any) => {
        const active = object?.active;
        const payload = object?.payload;
        const label = object?.label;
        if (active && payload && payload.length) {
            return (
                <div className={styles.tooltip}>
                    <span className="label">
                        <b>{`${label}`}</b>
                    </span>
                    <br />
                    {payload[0].payload.phasePeriod ? (
                        <span className="label">
                            <b>{`${payload[0].payload.phaseTitle}`}</b>
                        </span>
                    ) : (
                        <span className="label">
                            <b>Answer: {`${payload[0].value}`}</b>
                        </span>
                    )}
                    <br />
                    <span className="label">
                        <b>{payload[0].payload.activePhase ? 'Phase: ' + payload[0].payload.activePhase?.title : ''}</b>
                    </span>
                    <br />
                </div>
            );
        }

        return null;
    };

    function renderReferenceLine(x: any) {

        return (
            <ReferenceLine
                alwaysShow={false}
                x={x}
                isFront={true}
                stroke="#055"
                fill="#055"
                strokeWidth={5}
                ifOverflow={'hidden'}
            />
        );
    }

    //should be ordered by date in ASC
    return (
        <div className={styles.chart}>
            <div className={styles.plotContainer}>
                <button className={styles.download} onClick={handleDownload}>
                    <FontAwesomeIcon icon={faDownload} />
                    <b>{isLoading ? 'Downloading...' : 'Download'}</b>
                </button>
                <ResponsiveContainer>
                    <AreaChart
                        ref={areaRef}
                        data={data}
                        margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="seanceDate" />
                        <YAxis />

                        {target?.isPhased
                            ? target.programTargetPhases.map((phase: any) => {
                                  if (phase?.startDate) {
                                      return (
                                          <ReferenceLine
                                              alwaysShow={false}
                                              x={moment(phase?.startDate, 'YYYY-MM-DD HH:mm:ss').format('ll')}
                                              isFront={true}
                                              stroke="#055"
                                              fill="#055"
                                              strokeWidth={5}
                                              ifOverflow={'hidden'}
                                          />
                                      );
                                  } else {
                                      return null;
                                  }
                              })
                            : null}

                        {target?.isPhased
                            ? target.programTargetPhases.map((phase: any) => {
                                  if (phase?.endDate) {
                                      return (
                                          <ReferenceLine
                                              alwaysShow={false}
                                              x={moment(phase?.endDate, 'YYYY-MM-DD HH:mm:ss').format('ll')}
                                              isFront={true}
                                              stroke="#055"
                                              fill="#055"
                                              strokeWidth={5}
                                              ifOverflow={'hidden'}
                                          />
                                      );
                                  } else {
                                      return null;
                                  }
                              })
                            : null}

                        <Tooltip content={<CustomTooltip />} />
                        <Area type="linear" dataKey="answer" stroke="#8884d8" fill="none" activeDot={{ r: 3 }} />
                        <Brush dataKey="date" startIndex={data.length - 2}>
                            <AreaChart>
                                <CartesianGrid />
                                <YAxis hide domain={['auto', 'auto']} />
                                <Area type="linear" dataKey="answer" stroke="#8884d8" fill="none" dot={false} />
                            </AreaChart>
                        </Brush>
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default Plot;
